import {
  initPersister,
  NumberSource,
  persisterDeserialize,
  QueryFactory,
} from '@lmt/shared-types';
import {
  AppointmentColorSettingsDto,
  AppointmentStatusCodes,
  AppointmentType,
  VisitListDto,
} from '@lmt/shared-types';
import { convertDateTimeValuesInFilters } from '@lmt/unicorn-shared/app/components/calendar/color-settings/list/components/ConditionParser';
import {
  defaultShouldDehydrateQuery,
  QueryClient,
} from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  Persister,
  persistQueryClient,
} from '@tanstack/react-query-persist-client';
import { sortResources } from './sort';
import Logger from 'js-logger';
import { PatientNotFoundError } from '../components/patients/profile/types';
import { DocumentNotFoundError } from './error-types';
import { __getPatient } from '@lmt/shared-types/src/api/api-client/PatientQuery';
import { __getFileInfo } from '@lmt/shared-types/src/api/api-client/FileQuery';

export function configureReactQueryCache(queryClient: QueryClient): Persister {
  initPersister();
  const localStoragePersister = createSyncStoragePersister({
    storage: window.localStorage,

    // You need to import `persisterDeserialize` function from your `api-client.ts` and specify it as a deserialize function.
    deserialize: persisterDeserialize,
  });
  const defaultStaleTime = 1000 * 60 * 5; // 5 minutes

  const [subscribe, restorePromise] = persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 3 * 24 * 60 * 60 * 1000, //3 days

    dehydrateOptions: {
      shouldDehydrateMutation(mutation) {
        return false;
      },
      shouldDehydrateQuery(query) {
        if (query.meta?.['doNotCache']) return false;
        // we need to do this to fix Bug 65391: App version is incompatible with Backend after update
        // query.meta?.['doNotCache'] doesn't work, since in previous version (and also in cache) this meta flag wasn't set
        if (
          query.queryHash ===
            JSON.stringify(
              QueryFactory.UserQuery.getCurrentUserInfoQueryKey(),
            ) ||
          query.queryHash ===
            JSON.stringify(QueryFactory.VersionQuery.versionQueryKey()) ||
          query.queryHash ===
            JSON.stringify(
              QueryFactory.VersionQuery.versionAuthenticatedQueryKey(),
            )
        )
          return false;

        return defaultShouldDehydrateQuery(query);
      },
    },
  });
  restorePromise
    .then(async () => {
      await queryClient.invalidateQueries();
      setDefaultTimeouts();
    })
    .catch((e) => Logger.error(e));

  // we need to do it 'asynchronously', otherwise queries are not yet loaded from cache, thus, are not invalidated

  function setDefaultTimeouts() {
    const defaultCacheOptions = {
      gcTime: Infinity,
      staleTime: defaultStaleTime,
    };

    handle404();

    QueryFactory.UIConfigurationQuery.setGetConfigurationDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.UIConfigurationQuery.setGetPatientOverviewConfigurationsDefaultOptions(
      {
        ...defaultCacheOptions,
      },
    );
    QueryFactory.VisitTypeQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.RoleQuery.setGetRolesDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.TenantQuery.setGetSubtenantsDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.HL7TemplateRulesQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.GDTTemplateRulesQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.GDTSettingsQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.HL7SettingsQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.UserQuery.setGetUsersDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.UserQuery.setGetCurrentUserInfoDefaultOptions({
      meta: {
        doNotCache: true,
      },
    });
    QueryFactory.DataBlockDictionaryQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.DoctorQuery.setGetDoctorDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.DoctorQuery.setGetDoctorsDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.DataBlockTypeQuery.setGetDataBlockTypesForEditingDefaultOptions(
      {
        staleTime: defaultStaleTime,
      },
    );
    QueryFactory.DataBlockTypeQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    // otherwise, it's re-requested every time DataBlock is switched between ReadOnly to Edit mode
    QueryFactory.DataBlockFieldQuery.setGetByIdDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.DataBlockFieldQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });
    QueryFactory.TagQuery.setGetDefaultOptions({
      ...defaultCacheOptions,
    });

    QueryFactory.AppointmentColorSettingsQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
      queryFn: getColorSettings,
    });

    QueryFactory.HL7SettingsQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.HL7OutgoingSettingsQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });

    // documents caches
    QueryFactory.MergeFieldPrefixQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.MergeFieldQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.GDTOutgoingTemplatesQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });

    //cases caches
    QueryFactory.DataBlockDictionaryItemQuery.setGetDefaultOptions({
      staleTime: defaultStaleTime,
    });
    QueryFactory.DataBlockTypeQuery.setGetUIConfigsDefaultOptions({
      staleTime: defaultStaleTime,
    });

    //resources caches
    QueryFactory.ResourceQuery.setGetResourcesDefaultOptions({
      staleTime: defaultStaleTime,
      select: (data) => {
        data.data = data.data.sort(sortResources);
        return data;
      },
    });
  }

  return localStoragePersister;
}

/*
 We need a dummy appointment to pass it to convertDateTimeValuesInFilters.
 See comments to convertDateTimeValuesInFilters function for details.
 */
export const dummyAppointment: VisitListDto = {
  start: new Date(),
  end: new Date(),
  appointmentType: AppointmentType.Visit,
  caseId: 0,
  id: 0,
  note: '',
  contacts: [],
  patientId: 1,
  isDeleted: false,
  participants: [],
  patientName: '',
  priorityContact: '',
  resourceIds: [],
  statusId: AppointmentStatusCodes.Canceled,
  subtenantId: 1,
  title: '',
  typeId: 1,
  inWaitingList: false,
  priority: null,
  tags: [],
  importId: null,
  discriminator: 'VisitListDto',
  lastPatientNotificationDateTime: null,
  isPrivateInsurance: false,
  isSelfPayer: false,
  number: '1',
  numberSource: NumberSource.Autogenerated,
  attendingDoctorId: null,
};

async function getColorSettings() {
  const data = await QueryFactory.AppointmentColorSettingsClient.get();
  return selectParsedColorRule(data);
}

function selectParsedColorRule(colorSettingsDto: AppointmentColorSettingsDto) {
  colorSettingsDto.rules.forEach((rule: any) => {
    rule.parsedFilterExpression = rule.filterExpression
      ? JSON.parse(rule.filterExpression)
      : null;

    convertDateTimeValuesInFilters(
      rule.parsedFilterExpression,
      dummyAppointment,
    );
  });
  return colorSettingsDto;
}
/*
 * Throw custom exceptions when receive 404.
 * It is needed to show custom error instead of `An unexpected server error occured`
 */
function handle404() {
  const getPatientDefaultOptions =
    QueryFactory.PatientQuery.getGetPatientDefaultOptions();
  QueryFactory.PatientQuery.setGetPatientDefaultOptions({
    queryFn: async (...args) => {
      try {
        return await __getPatient(...args);
      } catch (e) {
        if ((e as any).status === 404) {
          throw new PatientNotFoundError();
        }
        throw e;
      }
    },
  });

  const getDocumentDefaultOptions =
    QueryFactory.FileQuery.getGetFileInfoDefaultOptions();
  QueryFactory.FileQuery.setGetFileInfoDefaultOptions({
    queryFn: async (...args) => {
      try {
        return await __getFileInfo(...args);
      } catch (e) {
        if ((e as any).status === 404) {
          throw new DocumentNotFoundError();
        }
        throw e;
      }
    },
  });
}
